/**
 * What an equipment asset tag encodes, and how to read one back.
 *
 * SPLIT OUT OF `equipmentLabels.ts` FOR ONE REASON: the mobile scan flow has to
 * resolve a scanned tag, and `equipmentLabels` reaches `labelLayout` →
 * `labelSymbol` → `bwip-js`. That is a symbol GENERATOR, useless to a surface
 * that only reads codes, and pulling it into the `/m` entry would spend a large
 * slice of the 250 KB budget on a dependency the phone never calls. This module
 * imports nothing.
 *
 * `equipmentLabels.ts` re-exports both functions, so there is still exactly one
 * definition: the value the printer writes and the value the scanner checks can
 * never drift apart, which is the property the original docblock was protecting.
 *
 * DERIVED FROM `Equipment.id`, NOT FROM `Equipment.label`. `label` is the
 * "Equipment ID" a person types (`APH-A4`) and it is editable forever; encode
 * that and renaming a unit silently invalidates every tag already stuck to
 * hardware. `Equipment.id` is a cuid assigned at creation and never changes.
 */

/** Prefix, mirroring the `LOC-` convention the pick list already established. */
const EQUIPMENT_PREFIX = 'EQP-'

export function equipmentScanCode(equipmentId: string | null | undefined): string | null {
  const id = equipmentId?.trim()
  if (!id) return null
  return `${EQUIPMENT_PREFIX}${id}`
}

/**
 * Whether a scanned string is an equipment tag, and which unit it names.
 *
 * Returns null for anything that is not an equipment code — a lot or location
 * scan reaching an equipment field is a mis-scan and must not resolve.
 *
 * CASE IS PRESERVED IN THE RETURNED ID, deliberately. The prefix test is
 * case-insensitive because a scanner or a typing operator may shift it, but
 * `Equipment.id` is a cuid and case-sensitive, so the id itself is sliced out of
 * the original string. Never feed this the output of `normalizeScanValue` —
 * that upper-cases, and the id it hands back will match no row.
 */
export function equipmentIdFromScan(scanned: string | null | undefined): string | null {
  const value = scanned?.trim()
  if (!value) return null
  if (!value.toUpperCase().startsWith(EQUIPMENT_PREFIX)) return null
  const id = value.slice(EQUIPMENT_PREFIX.length).trim()
  return id || null
}
